import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTabActive } from "../../lib/tabActive";
import { Icon } from "../ui/Icon";
import { ToggleSwitch } from "../ui/ToggleSwitch";

export type OrgUnitType = "company" | "division" | "department" | "team";

const TYPE_OPTIONS: Array<{ value: OrgUnitType; label: string }> = [
  { value: "company", label: "Şirket" },
  { value: "division", label: "Bölge / Direktörlük" },
  { value: "department", label: "Departman" },
  { value: "team", label: "Ekip" },
];

interface Props {
  open: boolean;
  title: string;
  initial: { name: string; type: OrgUnitType; isShiftBased?: boolean } | null; // null = yeni
  busy: boolean;
  showShiftToggle?: boolean;
  onClose: () => void;
  onSubmit: (data: { name: string; type: OrgUnitType; isShiftBased: boolean }) => void;
}

/** Birim ekle/düzenle — küçük ortalanmış modal (ConfirmDialog portal deseni). */
export function OrgUnitFormModal({ open, title, initial, busy, showShiftToggle, onClose, onSubmit }: Props) {
  const [name, setName] = useState("");
  const [type, setType] = useState<OrgUnitType>("department");
  const [isShiftBased, setIsShiftBased] = useState(true);
  const wasOpen = useRef(false);

  // YALNIZ açılış anında (kapalı→açık) başlangıç değeriyle doldur. `initial` her render'da yeni
  // nesne olduğundan bağımlılığa konmaz; aksi halde başarısız kayıt sonrası re-render kullanıcının
  // yazdığını eski değere geri yazardı.
  useEffect(() => {
    if (open && !wasOpen.current) {
      setName(initial?.name ?? "");
      setType(initial?.type ?? "department");
      setIsShiftBased(initial?.isShiftBased ?? true);
    }
    wasOpen.current = open;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const tabActive = useTabActive();
  if (!open) return null;
  // PASİF SEKMEDE DOM'A BASILMAZ. Portal `document.body`ye gider, yani sekme panelinin
  // `display:none` gizlemesi bu katmana ULAŞMAZ — gizli sekmenin paneli aktif sayfanın
  // üstünde durmaya devam ederdi. Burada `null` dönmek DOM'u düşürür, katmanı açan durum
  // (hangi kayıt, forma ne yazıldı) sayfanın React state'inde YAŞAMAYA DEVAM eder.
  if (!tabActive) return null;


  const canSubmit = name.trim().length > 0 && !busy;
  const submit = () => { if (canSubmit) onSubmit({ name: name.trim(), type, isShiftBased }); };

  return createPortal(
    <div className="cdlg-backdrop" role="presentation" onClick={onClose}>
      <div className="org-modal" role="dialog" aria-modal="true" aria-label={title} onClick={(e) => e.stopPropagation()}>
        <div className="org-modal-head"><Icon name="building" size={18} /><span>{title}</span></div>
        <div className="org-modal-body">
          <div className="form-field">
            <label className="lbl" htmlFor="org-name">Birim Adı</label>
            <input id="org-name" className="form-input" value={name} autoFocus maxLength={160}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
              placeholder="örn. Üretim Departmanı" />
          </div>
          <div className="form-field">
            <label className="lbl" htmlFor="org-type">Tür</label>
            <select id="org-type" className="form-select" value={type} onChange={(e) => setType(e.target.value as OrgUnitType)}>
              {TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          {showShiftToggle ? (
            <div className="form-field">
              <span className="lbl">Vardiyaya tabi</span>
              <ToggleSwitch checked={isShiftBased} onChange={setIsShiftBased}
                label={isShiftBased ? "Evet — vardiya planında görünür" : "Hayır — vardiya planında görünmez"} />
              <span className="help">
                Kapatırsanız bu birim ve ALT BİRİMLERİ vardiya planından çıkar. Puantaj ve bordro etkilenmez.
              </span>
            </div>
          ) : null}
        </div>
        <div className="cdlg-foot">
          <button type="button" className="btn-md ghost" onClick={onClose}>Vazgeç</button>
          <button type="button" className="btn-md primary" disabled={!canSubmit} onClick={submit}>
            {busy ? "Kaydediliyor…" : "Kaydet"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
